import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ResponseModel, { PaginationRes } from "../../models/response.model";
import {
  createCampaignApi,
  editCampaignApi,
  getCampaignsApi,
  getMsgsApi,
  pauseCampaignApi,
  resumeCampaignApi,
} from "../../api/whatsappCampaign.api";
import WhatsappCampaign from "../../models/whatsappCampaigns/campaign.model";
import CampaignMessageDocument from "../../models/whatsappCampaigns/campaignMsgs.model";

interface WhatsappCampaignState {
  campaigns?: WhatsappCampaign[];
  msgs: CampaignMessageDocument[];
  editedCampaignDetails?: {
    skippedContacts: { row: any; reason: string; mobileNumber: string }[];
    insertedCount: number;
    skippedCount: number;
  };
  loading: {
    createCampaign: boolean;
    fetchCampaigns: boolean;
    editCampaign: boolean;
    pauseCampaign: boolean;
    resumeCampaign: boolean;
    fetchMsgs: boolean;
  };
  pagination: {
    fetchCampaigns?: PaginationRes;
    fetchMsgs?: PaginationRes;
  };
}

const initialState: WhatsappCampaignState = {
  loading: {
    createCampaign: false,
    fetchCampaigns: false,
    editCampaign: false,
    pauseCampaign: false,
    resumeCampaign: false,
    fetchMsgs: false,
  },
  pagination: {},
  msgs: [],
};
export const fetchCampaigns = createAsyncThunk<
  ResponseModel,
  {
    page: number;
    limit: number;
  },
  { rejectValue: string }
>(
  "whatsappCampaign/fetchCampaigns",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      const data = await getCampaignsApi(page, limit);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);
export const fetchMsgs = createAsyncThunk<
  ResponseModel,
  {
    campaignId: string;
    page: number;
    limit: number;
    search?: string;
  },
  { rejectValue: string }
>(
  "whatsappCampaign/fetchMsgs",
  async ({ campaignId, page, limit, search }, { rejectWithValue }) => {
    try {
      const data = await getMsgsApi(campaignId, page, limit, search);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);

export const createCampaign = createAsyncThunk<
  ResponseModel,
  {
    campaignData: FormData;
  },
  { rejectValue: string }
>(
  "whatsappCampaign/createCampaign",
  async ({ campaignData }, { rejectWithValue }) => {
    try {
      const data = await createCampaignApi(campaignData);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);
export const editCampaign = createAsyncThunk<
  ResponseModel,
  {
    campaignId: string;
    campaignData: FormData;
  },
  { rejectValue: string }
>(
  "whatsappCampaign/editCampaign",
  async ({ campaignId, campaignData }, { rejectWithValue }) => {
    try {
      const data = await editCampaignApi(campaignId, campaignData);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);

export const pauseCampaign = createAsyncThunk<
  ResponseModel,
  {
    campaignId: string;
  },
  { rejectValue: string }
>(
  "whatsappCampaign/pauseCampaign",
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const data = await pauseCampaignApi(campaignId);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);

export const resumeCampaign = createAsyncThunk<
  ResponseModel,
  {
    campaignId: string;
  },
  { rejectValue: string }
>(
  "whatsappCampaign/resumeCampaign",
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const data = await resumeCampaignApi(campaignId);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch Kanban data");
    }
  },
);

const whatsappCampaignSlice = createSlice({
  name: "whatsappCampaign",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createCampaign.pending, (state) => {
        state.loading.createCampaign = true;
      })
      .addCase(createCampaign.fulfilled, (state) => {
        state.loading.createCampaign = false;
      })
      .addCase(createCampaign.rejected, (state) => {
        state.loading.createCampaign = false;
      })
      .addCase(editCampaign.pending, (state) => {
        state.loading.editCampaign = true;
      })
      .addCase(editCampaign.fulfilled, (state, action) => {
        state.loading.editCampaign = false;
        state.editedCampaignDetails = action.payload.data;
      })
      .addCase(editCampaign.rejected, (state) => {
        state.loading.editCampaign = false;
      })
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading.fetchCampaigns = true;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading.fetchCampaigns = false;
        state.campaigns = action.payload.data as WhatsappCampaign[];
        state.pagination.fetchCampaigns = action.payload.pagination;
      })
      .addCase(fetchCampaigns.rejected, (state) => {
        state.loading.fetchCampaigns = false;
      })
      .addCase(fetchMsgs.pending, (state) => {
        state.loading.fetchMsgs = true;
      })
      .addCase(fetchMsgs.fulfilled, (state, action) => {
        state.loading.fetchMsgs = false;
        state.msgs = action.payload.data;
        state.pagination.fetchMsgs = action.payload.pagination;
      })
      .addCase(fetchMsgs.rejected, (state) => {
        state.loading.fetchMsgs = false;
      })
      .addCase(pauseCampaign.pending, (state) => {
        state.loading.pauseCampaign = true;
      })
      .addCase(pauseCampaign.fulfilled, (state) => {
        state.loading.pauseCampaign = false;
      })
      .addCase(pauseCampaign.rejected, (state) => {
        state.loading.pauseCampaign = false;
      })
      .addCase(resumeCampaign.pending, (state) => {
        state.loading.resumeCampaign = true;
      })
      .addCase(resumeCampaign.fulfilled, (state) => {
        state.loading.resumeCampaign = false;
      })
      .addCase(resumeCampaign.rejected, (state) => {
        state.loading.resumeCampaign = false;
      });
  },
});

export default whatsappCampaignSlice.reducer;
